import { useMemo, useCallback } from 'react'
import { useAuth } from '@/hooks/useAuth'
import { useGroups } from '@/hooks/useGroups'
import { useAllTags } from '@/hooks/useTags'
import { useUserGroups } from '@/hooks/useGroupMemberships'
import { filterAccessibleItems } from '@/utils/accessControl'

type TagField = 'taskId' | 'reasonId'

export function useAccessControlFilterFn(tagField: TagField) {
  const { user, churchId, role } = useAuth()
  const { data: groups } = useGroups()
  const { data: allTags } = useAllTags()
  const { data: memberships } = useUserGroups(user?.uid, churchId ?? undefined)

  const isVolunteerRole = role === 'volunteer'
  const isReady =
    !!role &&
    !!user &&
    (isVolunteerRole || (!!allTags && !!groups && !!memberships))

  const filterItems = useCallback(
    <T extends { id: string }>(items: T[]): T[] => {
      if (!isReady) return items
      return filterAccessibleItems(
        items,
        user!.uid,
        role!,
        isVolunteerRole ? [] : allTags!,
        isVolunteerRole ? [] : groups!,
        isVolunteerRole ? [] : memberships!,
        tagField,
      )
    },
    [
      isReady,
      user,
      role,
      allTags,
      groups,
      memberships,
      tagField,
      isVolunteerRole,
    ],
  )

  return { filterItems, isReady }
}

export function useAccessControlledItem<T extends { id: string }>(
  item: T | undefined,
  tagField: TagField,
) {
  const { filterItems, isReady } = useAccessControlFilterFn(tagField)

  return useMemo(() => {
    if (!item || !isReady) return { data: item, isAccessDenied: false }
    const accessible = filterItems([item])
    if (accessible.length === 0)
      return { data: undefined as T | undefined, isAccessDenied: true }
    return { data: accessible[0], isAccessDenied: false }
  }, [item, isReady, filterItems])
}
